import { ScreenType } from '@accedo/one-plugin-sdk';
import { useCallback } from 'react';
import type { Snapshot } from 'valtio';
import { snapshot } from 'valtio';

import { oneObject } from '#/bootstrap/oneObject';
import {
  Collection,
  CollectionData,
  DeviceInfo,
  FeedResponse,
  Item,
  LayoutData,
  MediaResource,
  Platform,
  Profile,
  ProfileResponse,
  ResourceItem,
  ResourceType,
} from '#/client/models';
import { errorSpecVersion } from '#/errors/errors';
import { OneErrorDetails } from '#/errors/oneError';
// useConfig is not a "real" hook, but ESLint prevents using it outside React.
// this little workaround allows it to be used in the error context that is needed outside React too.
import { useConfig as getConfig } from '#/hooks/useConfig';
import { ThemeConfig } from '#/service/config/config.types';
import { CoreFeatureConfig } from '#/service/config/features.types';
import { getUsPrivacyString } from '#/service/user/settings';
import {
  getDeviceId,
  getDeviceInfo,
  getPlatform,
} from '#/service/user/userSessionManager';
import { getQueryParamFromStorage, SearchParams } from '#/service/utils';
import { authStore, useAuthStore } from '#/store/auth/auth.store';
import {
  favoritesStore,
  useFavoritesStore,
} from '#/store/favorites/favorites.store';

const buildGeneralContext = ({
  appId,
  name,
  version,
  theme,
  featureConfig,
  isLoggedIn,
  isEntitled,
  profile,
  userId,
  profileResponse,
  favoriteItemIds,
}: {
  appId: string;
  name: string;
  version: string;
  theme: ThemeConfig;
  featureConfig: CoreFeatureConfig;
  isLoggedIn: boolean;
  isEntitled: boolean;
  profile: Snapshot<Profile>;
  userId?: string;
  profileResponse?: Snapshot<ProfileResponse>;
  favoriteItemIds: Snapshot<string[]>;
}): GeneralContext => ({
  app: {
    id: appId,
    name,
    version,
    internalVersion: version,
    // container visibility conditions depend on this being `undefined` if not set
    campaignTag: getQueryParamFromStorage(
      SearchParams.CampaignTag,
      sessionStorage
    ),
    /** IAB US Privacy string (USP format, e.g. "1NNN" or "1NYN") reflecting the user's CCPA opt-out preference. */
    usPrivacy: getUsPrivacyString(),
  },
  user: {
    // the old requirement was just to spread the attributes here, ex: user.email
    // this is deprecated, but we have to keep it because current "more" configs are using it like this.
    ...profile?.attributes,
    // the new requirement is to include everything like it is in the profile response, ex: user.profile.attributes.email
    ...profileResponse,
    // computed & state
    isLoggedIn,
    isEntitled,
    favoriteItemIds,
    id: userId ?? '',
  },
  featureConfig,
  deviceInfo: getDeviceInfo(),
  platform: getPlatform(),
  theme,
});

/**
 * Builds a OneErrorContext without React hooks, for use outside of components.
 * Reads current values directly from the Valtio store proxies (as opposed to snapshots, like useAuthStore) and config,
 * and makes its own snapshots to "un-proxy" all the nested values.
 */
export const buildOneErrorContext = (
  code: string,
  details: OneErrorDetails = {},
  extras?: Record<string, unknown>
): OneErrorContext => {
  const {
    app: { name, version = '' },
    theme,
    featureConfig,
  } = getConfig();
  const { isLoggedIn, isEntitled, profile, userId, profileResponse } =
    snapshot(authStore);
  const { favoriteIds: favoriteItemIds } = snapshot(favoritesStore);
  const {
    cdnRequestIds = [],
    relatedErrorCodes = [],
    requestIds = [],
    extras: detailExtras = {},
  } = details;

  return {
    ...buildGeneralContext({
      appId: oneObject.appId ?? '',
      name,
      version,
      theme,
      featureConfig,
      isLoggedIn,
      isEntitled,
      profile,
      userId,
      profileResponse,
      favoriteItemIds,
    }),
    code,
    timestamp: new Date().toISOString(),
    errorSpecVersion,
    variationHash: oneObject.variationHash ?? '',
    deviceId: getDeviceId(),
    userId: userId ?? '',
    clientIp: profile?.device?.network?.ip ?? '',
    clientVersion: version,
    cdnRequestIds,
    relatedErrorCodes,
    requestIds,
    extras: { ...detailExtras, ...extras },
    cause: undefined,
  };
};

/**
 * This function is mainly in reference to this confluence page: https://accedobroadband.jira.com/wiki/spaces/AccedoOne/pages/1377173549/Data+Binding+DRAFT
 * This function also covers the user of web specific contexts, but in general we should be refactoring this function to utilize the logic we have described in the above link.
 * @returns Object with different context getter functions.
 */
export const useDataContext = () => {
  const {
    app: { name, version = '' },
    theme,
    featureConfig,
  } = getConfig();
  const { isLoggedIn, isEntitled, profile, userId, profileResponse } =
    useAuthStore();
  const { favoriteIds: favoriteItemIds } = useFavoritesStore();

  // this is included in all other contexts
  const getGeneralContext = useCallback(
    (): GeneralContext =>
      buildGeneralContext({
        appId: oneObject.appId ?? '',
        name,
        version,
        theme,
        featureConfig,
        isLoggedIn,
        isEntitled,
        profile,
        userId,
        profileResponse,
        favoriteItemIds,
      }),
    [
      favoriteItemIds,
      featureConfig,
      isEntitled,
      isLoggedIn,
      name,
      profile,
      profileResponse,
      theme,
      userId,
      version,
    ]
  );

  const getDetailsPageContext = (
    resourceType: string | undefined,
    resource: ResourceItem | Collection
  ): DetailsPageContext => ({
    ...getGeneralContext(),
    item:
      resourceType === ResourceType.Item
        ? (resource as ResourceItem).data
        : (resource as Collection).collection,
    upNext:
      resourceType === ResourceType.Collection
        ? (resource as Collection).collection?.upNext
        : undefined,
  });

  const getPageContext = useCallback(
    (
      prevContext: PageContext | undefined,
      defaultContext: object,
      layout?: LayoutData
    ): PageContext => {
      return {
        // ONEWEB-3042 - this shouldn't be needed in the context
        containerCollections: layout?.containerCollections,
        ...defaultContext,
        // this should be after `defaultContext`, so the logged out `defaultContext.user` don't overwrite the current user state.
        ...getGeneralContext(),
        // place for dynamically changeable context props
        lastUpdated: prevContext
          ? prevContext.lastUpdated
          : new Date().toISOString(),
        flags: prevContext?.flags ?? {},
        isHistoryFetched: prevContext?.isHistoryFetched ?? false,
      };
    },
    [getGeneralContext]
  );

  const getSearchContext = (
    searchTerm?: string,
    searchResults?: FeedResponse
  ): SearchContext => {
    return {
      ...getGeneralContext(),
      count: searchResults?.pageInfo?.total ?? 0,
      search: {
        query: searchTerm === '' ? undefined : searchTerm,
      },
      searchResults: {
        count: searchResults?.pageInfo?.total ?? 0,
        searchQuery: {
          text: searchTerm === '' ? undefined : searchTerm,
        },
      },
    };
  };

  const getWatchHistoryContext = (
    watchHistoryData: FeedResponse
  ): WatchHistoryContext => ({
    ...getGeneralContext(),
    count: watchHistoryData.data?.length ?? 0,
  });

  const getFavoritesContext = (
    favoritesData: FeedResponse
  ): FavoritesContext => ({
    ...getGeneralContext(),
    count: favoritesData.data?.length ?? 0,
  });

  const getUnauthenticatedHomePageContext =
    (): UnauthenticatedHomePageContext => ({
      ...getGeneralContext(),
      screenType: ScreenType.UnauthenticatedHome,
    });

  const getOneErrorContext = (
    code: string,
    details: OneErrorDetails = {}
  ): OneErrorContext => buildOneErrorContext(code, details);

  return {
    getDetailsPageContext,
    getPageContext,
    getGeneralContext,
    getSearchContext,
    getWatchHistoryContext,
    getFavoritesContext,
    getUnauthenticatedHomePageContext,
    getOneErrorContext,
  };
};

// included in all other contexts
export interface GeneralContext {
  app: {
    id: string;
    name: string;
    version: string;
    internalVersion: string;
    campaignTag: string | undefined;
    usPrivacy: string;
  };
  user: Partial<Snapshot<ProfileResponse>> & {
    // additional things we put into the user object when passing it into the contexts
    isLoggedIn: boolean;
    isEntitled: boolean;
    favoriteItemIds: readonly string[];
    id: string;
    // this is for supporting the deprecated way of passing attributes for backwards compatibility, ex: user.email
    // otherwise this would be just UserContext
    [key: string]: any;
  };
  featureConfig: CoreFeatureConfig;
  deviceInfo: DeviceInfo;
  platform: Platform;
  theme: ThemeConfig;
}

interface SearchContext extends GeneralContext {
  count: number;
  search: {
    query?: string;
  };
  searchResults: {
    count: number;
    searchQuery: {
      text?: string;
    };
  };
}

interface DetailsPageContext extends GeneralContext {
  item: Item | MediaResource;
  upNext?: Item;
}

export interface PageContext extends GeneralContext {
  lastUpdated: string;
  flags: Record<string, any>;
  isHistoryFetched: boolean;
  containerCollections?: CollectionData[];
}

interface WatchHistoryContext extends GeneralContext {
  count: number;
}

interface FavoritesContext extends GeneralContext {
  count: number;
}

interface UnauthenticatedHomePageContext extends GeneralContext {
  screenType: ScreenType;
}

export interface CancelContext extends GeneralContext {
  cancel: {
    isSaveCampaignEnabled: boolean;
    selectedProductId?: string;
    saveCampaignOfferText?: string;
  };
}

export interface OneErrorContext
  extends Omit<Required<OneErrorDetails>, 'preventSentry'>,
    GeneralContext {
  code: string;
  timestamp: string;
  errorSpecVersion: string;
  variationHash: string;
  deviceId: string;
  userId: string;
  clientIp: string;
  clientVersion: string;
}

export type GenericContext =
  | GeneralContext
  | SearchContext
  | DetailsPageContext
  | WatchHistoryContext
  | FavoritesContext
  | CancelContext
  | UnauthenticatedHomePageContext;
